<script>
import _ from 'lodash'

/**
 * A renderless component for sending form data to the server
 *
 * :src = the server URL to send the data. equivalent to action="" on a normal html form
 * :method = post/patch/delete/get/etc
 * :loader = set the function that performs the server comms. By default it uses $inertia (which uses axios)
 *
 * v-slot provides an object that has the following properties you can use in your form
 * - submit() - a function that when called sends data to the server
 *
 * - update() - set the form data that should be sent. Easiest way to use this is to catch the v-model update        event
 *
 * - errrors - an object containing validation errors we receive from the server. Pass it to your form to display this info to the user
 * <form-request :action="src" method="delete" v-slot="{ submit }">
 *     <button @click="submit()">Send data</button>
 * </form-request>
 *
 * example of how this component is used to delete a file
 * // In this example src is the "delete file" route
 *
 * <form-request :action="src" method="delete" v-slot="{ submit }">
 *     <confirm-danger title="Delete Media"
 *                     button="Delete"
 *                     :show="showModal"
 *                     @confirm="submit()"
 *                     @close="closeModal">
 *         Are you sure you want to delete {{ filename }}? <br/> This action cannot be undone
 *     </confirm-danger>
 * </form-request>
 *
 *
 * *****
 * Failure event
 * *****
 * There is a watcher on the computed errors property of this class. If this variable changes we emit an event called
 * "failure". You can use this event in your parent classes to do something if server validation of form data fails
 * using <form-request @failure="nameOfMethodToCallOnFail"></form-request>
 *
 */
export default {
  props: {
    /**
     * The URL we send the form to for processing
     */
    action: {
      type: String,
      required: true
    },

    /**
     * The http method to use
     */
    method: {
      type: String,
      default: 'post'
    },

    /**
     * The function to use to perform the http request. See getLoader method for more info
     */
    loader: {
      default: null
    }
  },

  data() {
    return { form: {} }
  },

  watch: {
    /**
     * Called if the submitted data didn't pass server validation. Emits a "failure" event that can be caught by
     * the parent class
     */
    errors() {
      if (Object.keys(this.errors) > 0) {
        // Emit failure but dont do it again for 10 seconds
        _.throttle(() => this.$emit('failure'), 5000)()
      }
    }
  },

  computed: {
    /**
     * A list of server validation errors
     * @returns {Errors & ErrorBag}
     */
    errors() {
      return this.$page.props.errors
    }
  },

  methods: {
    /**
     * Replace the form with a more up to date version
     * @param data a key=>value store of data we want to send to the server
     */
    updateForm(data) {
      this.form = data
    },

    /**
     * Submit the form to the server
     */
    submit() {
      this.getLoader()[this.method](this.action, this.form)
    },

    /**
     * Return the loader we use to perform the http request.
     */
    getLoader() {
      // If we have specified a custom loader use that instead of inertia
      if (this.loader) {
        return this.loader
      }

      return this.$inertia
    }
  },

  /**
   * Renders component to the browser page directly instead of using a <template> element
   * @param h an internal vue function that creates a component
   */
  render(h) {
    let slot = null
    try {
      slot = this.$slots.default({
        // legacy
        updateForm: this.updateForm,

        // A function that allows you to set the form params to send
        update: this.updateForm,

        // A list of errors that were found
        errors: this.errors,

        // A function that allows you to submit the form
        submit: this.submit
      })
    } catch (e) {}

    return slot
  }
}
</script>
